import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Component, Optional, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Observable, iif, of, lastValueFrom, map as map$1 } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as i1 from '@angular/cdk/platform';
import * as i2 from '@angular/router';
import * as i2$1 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as i1$1 from '@highfieldabc/angular-highfieldwebapi';
class CurrentUser {
  constructor(initialised, user) {
    this.initialised = initialised;
    this.user = user;
  }
}
class LoginUser {
  constructor(id, username, firstName, lastName, roles) {
    this.id = id;
    this.username = username;
    this.firstName = firstName;
    this.lastName = lastName;
    this.roles = roles;
  }
  hasRole(role) {
    let _return = false;
    if (Array.isArray(this.roles)) {
      for (var i = 0; i < this.roles.length; i++) {
        if (this.roles[i].toLowerCase().trim() === role.toLowerCase().trim()) {
          _return = true;
          break;
        }
      }
      return _return;
    } else {
      return this.roles.toLowerCase().trim() === role.toLowerCase().trim();
    }
  }
}
const UserbaseAuthStrategy = new InjectionToken('UserbaseAuthStrategy');
let CurrentUserService = /*#__PURE__*/(() => {
  class CurrentUserService {
    constructor(platformId, userbaseAuthStrategy, userApiBaseUrl) {
      this.platformId = platformId;
      this.userbaseAuthStrategy = userbaseAuthStrategy;
      this.userApiBaseUrl = userApiBaseUrl;
      this.currentUser = new CurrentUser(false, null);
      this.dispatcher = new BehaviorSubject(this.currentUser);
      this.currentTokenExpiry = new Date();
      this.helper = new JwtHelperService();
      this.loadCurrentUser();
    }
    async loadCurrentUser() {
      let currentToken = '';
      if (this.platformId.isBrowser) {
        currentToken = await this.userbaseAuthStrategy.getTokenForCurrentUser(this.userApiBaseUrl);
      }
      var user = null;
      if (currentToken && currentToken.split('.').length == 3) {
        try {
          var _token = this.helper.decodeToken(currentToken);
          user = new LoginUser(_token.nameid, _token.unique_name, _token.firstname, _token.lastname, _token.role);
          this.currentTokenExpiry = new Date(0);
          this.currentTokenExpiry.setUTCSeconds(_token.exp);
        } catch (ex) {
          throw new Error('Unable to load current user.');
        }
      } else {
        user = null;
      }
      this.setCurrentUser(user);
    }
    getCurrentUser() {
      return this.currentUser;
    }
    setCurrentUser(user) {
      if (user === null && this.currentUser.user === null && this.currentUser.initialised === false || user !== null && this.currentUser.user === null || user === null && this.currentUser.user !== null || user !== this.currentUser.user) {
        this.currentUser.user = user;
        this.currentUser.initialised = true;
        this.dispatcher.next(this.currentUser);
      }
    }
    async isValidUser() {
      let currentUser = this.getCurrentUser();
      if (currentUser.initialised == false || this.currentTokenExpiry < new Date()) {
        await this.loadCurrentUser();
        currentUser = this.getCurrentUser();
      }
      if (currentUser.user == null) {
        return false;
      } else {
        return true;
      }
    }
    static {
      this.ɵfac = function CurrentUserService_Factory(t) {
        return new (t || CurrentUserService)(i0.ɵɵinject(i1.Platform), i0.ɵɵinject(UserbaseAuthStrategy), i0.ɵɵinject('UserApiBaseUrl'));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CurrentUserService,
        factory: CurrentUserService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CurrentUserService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AuthCheckComponent = /*#__PURE__*/(() => {
  class AuthCheckComponent {
    constructor(currentUserService, route, router, displayName, authFailedUrl) {
      this.currentUserService = currentUserService;
      this.route = route;
      this.router = router;
      this.displayName = displayName;
      this.authFailedUrl = authFailedUrl;
      this.userWatcher = null;
    }
    ngOnInit() {
      this.userWatcher = this.currentUserService.dispatcher.subscribe(user => {
        if (user.initialised === true) {
          this.redirectToPath();
        }
      });
    }
    redirectToPath() {
      if (this.currentUserService.getCurrentUser().user === null) {
        this.router.navigate([this.authFailedUrl], {
          queryParams: {
            returnUrl: this.route.snapshot.queryParams['returnUrl'] !== '/' ? this.route.snapshot.queryParams['returnUrl'] : null
          }
        });
      } else {
        this.router.navigate([this.route.snapshot.queryParams['returnUrl'] || '']);
      }
    }
    static {
      this.ɵfac = function AuthCheckComponent_Factory(t) {
        return new (t || AuthCheckComponent)(i0.ɵɵdirectiveInject(CurrentUserService), i0.ɵɵdirectiveInject(i2.ActivatedRoute), i0.ɵɵdirectiveInject(i2.Router), i0.ɵɵdirectiveInject('AuthCheckDisplayName'), i0.ɵɵdirectiveInject('AuthenticatedCheckFailURL'));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: AuthCheckComponent,
        selectors: [["highfieldauth-authcheck"]],
        features: [i0.ɵɵProvidersFeature([])],
        decls: 5,
        vars: 1,
        consts: [["id", "AuthMessage"]],
        template: function AuthCheckComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0)(1, "h2");
            i0.ɵɵtext(2);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(3, "h3");
            i0.ɵɵtext(4, "Please wait while we confirm your authentication details.");
            i0.ɵɵelementEnd()();
          }
          if (rf & 2) {
            i0.ɵɵadvance(2);
            i0.ɵɵtextInterpolate1("Welcome To ", ctx.displayName, "");
          }
        },
        styles: ["#AuthMessage[_ngcontent-%COMP%]{max-width:400px;margin-left:auto;margin-right:auto;text-align:center}"]
      });
    }
  }
  return AuthCheckComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class ValidEmailCheck {
  constructor() {
    this.userId = '';
    this.emailAddress = '';
  }
}
class UserRoleDto {
  constructor() {
    this.userId = '';
    this.roleId = '';
  }
}
var GrantResponse = /*#__PURE__*/function (GrantResponse) {
  GrantResponse[GrantResponse["Success"] = 0] = "Success";
  GrantResponse[GrantResponse["InvalidUser"] = 1] = "InvalidUser";
  GrantResponse[GrantResponse["IncorrectPassword"] = 2] = "IncorrectPassword";
  GrantResponse[GrantResponse["BlacklistedUser"] = 3] = "BlacklistedUser";
  GrantResponse[GrantResponse["AccountLocked"] = 4] = "AccountLocked";
  GrantResponse[GrantResponse["RequiresTwoFactorAuthentication"] = 5] = "RequiresTwoFactorAuthentication";
  GrantResponse[GrantResponse["RequiresTwoFactorAuthenticationSetup"] = 6] = "RequiresTwoFactorAuthenticationSetup";
  GrantResponse[GrantResponse["TwoFactorAuthenticationFailed"] = 7] = "TwoFactorAuthenticationFailed";
  GrantResponse[GrantResponse["InvalidRefreshToken"] = 8] = "InvalidRefreshToken";
  GrantResponse[GrantResponse["InvalidOrigin"] = 9] = "InvalidOrigin";
  GrantResponse[GrantResponse["Timeout"] = 10] = "Timeout";
  return GrantResponse;
}(GrantResponse || {});
const AuthTokenPrefix = new InjectionToken('AuthTokenPrefix');
const V1AuthTokenPrefix = 'authToken_v1_';
const V2AuthTokenPrefix = 'authToken_v2_';
const RefreshTokenName = new InjectionToken('RefreshTokenName');
const V1RefreshTokenName = 'refreshToken_v1';
const V2RefreshTokenName = 'refreshToken_v2';
class BaseUserbaseAuthStrategy {
  constructor(easyXDMService, serverUrl) {
    this.easyXDMService = easyXDMService;
    this.serverUrl = serverUrl;
  }
  async getAuthTokenAsync(baseUrl) {
    const returnString = await this.easyXDMService.MsgPromise(this.serverUrl, baseUrl, 'DistLogin');
    return returnString;
  }
}
let V1UserbaseAuthStrategy = /*#__PURE__*/(() => {
  class V1UserbaseAuthStrategy extends BaseUserbaseAuthStrategy {
    constructor(easyXDMService, serverUrl) {
      super(easyXDMService, serverUrl);
      this.easyXDMService = easyXDMService;
      this.serverUrl = serverUrl;
    }
    async getTokenForCurrentUser(baseUrl) {
      return await this.getAuthTokenAsync(baseUrl);
    }
    getAuthToken(baseUrl) {
      let remoteData;
      remoteData = new Observable(observer => {
        this.easyXDMService.MsgObservable(this.serverUrl, baseUrl, 'DistLogin').subscribe(value => {
          observer.next(value);
          observer.complete();
        });
      });
      return remoteData;
    }
    static {
      this.ɵfac = function V1UserbaseAuthStrategy_Factory(t) {
        return new (t || V1UserbaseAuthStrategy)(i0.ɵɵinject(i1$1.EasyXDMService), i0.ɵɵinject('AuthApiBaseUrl'));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: V1UserbaseAuthStrategy,
        factory: V1UserbaseAuthStrategy.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return V1UserbaseAuthStrategy;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AuthenticatedService = /*#__PURE__*/(() => {
  class AuthenticatedService {
    constructor(apiService, settingService, currentUserService, userbaseAuthStrategy, serverUrl,
    // This is only needed because of the iFrame functionality for Backwards Compatibility
    v1UserBaseAuthStrategy, authTokenPrefix, refreshTokenName) {
      this.apiService = apiService;
      this.settingService = settingService;
      this.currentUserService = currentUserService;
      this.userbaseAuthStrategy = userbaseAuthStrategy;
      this.serverUrl = serverUrl;
      this.v1UserBaseAuthStrategy = v1UserBaseAuthStrategy;
      this.authTokenPrefix = authTokenPrefix;
      this.refreshTokenName = refreshTokenName;
    }
    //#region Promise functions
    async getAsync(serviceBaseURL, requestUri, contentType = 'application/json') {
      const authToken = this.settingService.getSetting(`${this.authTokenPrefix}${serviceBaseURL}`);
      let headers = new HttpHeaders({
        'Content-Type': contentType,
        'Authorization': 'Bearer ' + authToken
      });
      const response = await new Promise(resolve => {
        this.apiService.getAsync(serviceBaseURL, requestUri, headers).then(res => {
          resolve(res);
        }, async err => {
          if (err.responseCode === 401) {
            const newAuthToken = await this.userbaseAuthStrategy.getAuthTokenAsync(serviceBaseURL);
            if (newAuthToken) {
              this.settingService.setSetting(`${this.authTokenPrefix}${serviceBaseURL}`, newAuthToken);
              headers = headers.set('Authorization', `Bearer ${newAuthToken}`);
              this.apiService.getAsync(serviceBaseURL, requestUri, headers).then(authedRes => {
                resolve(authedRes);
              }, async authedErr => {
                if (authedErr.responseCode === 401) {
                  await this.currentUserService.loadCurrentUser();
                }
                resolve(authedErr);
              });
            } else {
              await this.currentUserService.loadCurrentUser();
              resolve(err);
            }
          } else {
            resolve(err);
          }
        });
      });
      return response;
    }
    async postAsync(serviceBaseURL, requestUri, body, contentType = 'application/json') {
      const authToken = this.settingService.getSetting(`${this.authTokenPrefix}${serviceBaseURL}`);
      let headers = new HttpHeaders({
        'Content-Type': contentType,
        'Authorization': 'Bearer ' + authToken
      });
      const response = await new Promise(resolve => {
        this.apiService.postAsync(serviceBaseURL, requestUri, headers, body).then(res => {
          resolve(res);
        }, async err => {
          if (err.responseCode === 401) {
            const newAuthToken = await this.userbaseAuthStrategy.getAuthTokenAsync(serviceBaseURL);
            if (newAuthToken) {
              this.settingService.setSetting(`${this.authTokenPrefix}${serviceBaseURL}`, newAuthToken);
              headers = headers.set('Authorization', `Bearer ${newAuthToken}`);
              this.apiService.postAsync(serviceBaseURL, requestUri, headers, body).then(authedRes => {
                resolve(authedRes);
              }, async authedErr => {
                if (authedErr.responseCode === 401) {
                  await this.currentUserService.loadCurrentUser();
                }
                resolve(authedErr);
              });
            } else {
              await this.currentUserService.loadCurrentUser();
              resolve(err);
            }
          } else {
            resolve(err);
          }
        });
      });
      return response;
    }
    async putAsync(serviceBaseURL, requestUri, body, contentType = 'application/json') {
      const authToken = this.settingService.getSetting(`${this.authTokenPrefix}${serviceBaseURL}`);
      let headers = new HttpHeaders({
        'Content-Type': contentType,
        'Authorization': 'Bearer ' + authToken
      });
      const response = await new Promise(resolve => {
        this.apiService.putAsync(serviceBaseURL, requestUri, headers, body).then(res => {
          resolve(res);
        }, async err => {
          if (err.responseCode === 401) {
            const newAuthToken = await this.userbaseAuthStrategy.getAuthTokenAsync(serviceBaseURL);
            if (newAuthToken) {
              this.settingService.setSetting(`${this.authTokenPrefix}${serviceBaseURL}`, newAuthToken);
              headers = headers.set('Authorization', `Bearer ${newAuthToken}`);
              this.apiService.putAsync(serviceBaseURL, requestUri, headers, body).then(authedRes => {
                resolve(authedRes);
              }, async authedErr => {
                if (authedErr.responseCode === 401) {
                  await this.currentUserService.loadCurrentUser();
                }
                resolve(authedErr);
              });
            } else {
              await this.currentUserService.loadCurrentUser();
              resolve(err);
            }
          } else {
            resolve(err);
          }
        });
      });
      return response;
    }
    async deleteAsync(serviceBaseURL, requestUri, contentType = 'application/json') {
      const authToken = this.settingService.getSetting(`${this.authTokenPrefix}${serviceBaseURL}`);
      let headers = new HttpHeaders({
        'Content-Type': contentType,
        'Authorization': 'Bearer ' + authToken
      });
      const response = await new Promise(resolve => {
        this.apiService.deleteAsync(serviceBaseURL, requestUri, headers).then(res => {
          resolve(res);
        }, async err => {
          if (err.responseCode === 401) {
            const newAuthToken = await this.userbaseAuthStrategy.getAuthTokenAsync(serviceBaseURL);
            if (newAuthToken) {
              this.settingService.setSetting(`${this.authTokenPrefix}${serviceBaseURL}`, newAuthToken);
              headers = headers.set('Authorization', `Bearer ${newAuthToken}`);
              this.apiService.deleteAsync(serviceBaseURL, requestUri, headers).then(authedRes => {
                resolve(authedRes);
              }, async authedErr => {
                if (authedErr.responseCode === 401) {
                  await this.currentUserService.loadCurrentUser();
                }
                resolve(authedErr);
              });
            } else {
              await this.currentUserService.loadCurrentUser();
              resolve(err);
            }
          } else {
            resolve(err);
          }
        });
      });
      return response;
    }
    async getTypedAsync(serviceBaseURL, requestUri, contentType = 'application/json') {
      const response = await this.getAsync(serviceBaseURL, requestUri, contentType);
      return this.getResponse(response);
    }
    async postTypedAsync(serviceBaseURL, requestUri, body, contentType = 'application/json') {
      const response = await this.postAsync(serviceBaseURL, requestUri, body, contentType);
      return this.getResponse(response);
    }
    async putTypedAsync(serviceBaseURL, requestUri, body, contentType = 'application/json') {
      const response = await this.putAsync(serviceBaseURL, requestUri, body, contentType);
      return this.getResponse(response);
    }
    async getFileAsync(serviceBaseURL, requestUri, contentType = 'application/pdf') {
      const authToken = this.settingService.getSetting(`${this.authTokenPrefix}${serviceBaseURL}`);
      let headers = new HttpHeaders({
        'Content-Type': contentType,
        'Authorization': 'Bearer ' + authToken
      });
      const response = await new Promise(resolve => {
        this.apiService.getFileAsync(serviceBaseURL, requestUri, headers).then(res => {
          resolve(res);
        }, async err => {
          if (err.responseCode === 401) {
            const newAuthToken = await this.userbaseAuthStrategy.getAuthTokenAsync(serviceBaseURL);
            if (newAuthToken) {
              this.settingService.setSetting(`${this.authTokenPrefix}${serviceBaseURL}`, newAuthToken);
              headers = headers.set('Authorization', `Bearer ${newAuthToken}`);
              this.apiService.getFileAsync(serviceBaseURL, requestUri, headers).then(authedRes => {
                resolve(authedRes);
              }, authedErr => {
                resolve(authedErr);
              });
            } else {
              resolve(err);
            }
          } else {
            resolve(err);
          }
        });
      });
      return response;
    }
    getResponse(response) {
      if (response.responseCode === 200 || response.responseCode === 201 || response.responseCode === 204) {
        if (response.responseData == null || response.responseData == undefined) {
          return null;
        }
        return response.responseData;
      }
      if (response.responseCode === 400) {
        throw new Error(response.responseError);
      }
      if (response.responseCode === 401) {
        throw new Error("Unauthorised");
      }
      throw new Error("Internal server error");
    }
    //#endregion Promise functions
    //#region Observable functions
    getObservable(serviceBaseURL, requestUri, contentType = 'application/json') {
      const authTokenName = `${this.authTokenPrefix}${serviceBaseURL}`;
      const getRequest = headers => this.apiService.getObservable(serviceBaseURL, requestUri, headers);
      return getRequest(this.getHeaders(contentType, authTokenName)).pipe(catchError(error => iif(() => error.status === 401, this.unauthorisedFallback(serviceBaseURL, contentType, authTokenName, getRequest),
      //Refresh token and try again
      this.getDefaultError(error) // Non-401 error, return GenericAPIResponse with error messages
      )));
    }
    postObservable(serviceBaseURL, requestUri, body, contentType = 'application/json') {
      const authTokenName = `${this.authTokenPrefix}${serviceBaseURL}`;
      const postRequest = headers => this.apiService.postObservable(serviceBaseURL, requestUri, headers, body);
      return postRequest(this.getHeaders(contentType, authTokenName)).pipe(catchError(error => iif(() => error.status === 401, this.unauthorisedFallback(serviceBaseURL, contentType, authTokenName, postRequest),
      //Refresh token and try again
      this.getDefaultError(error) // Non-401 error, return GenericAPIResponse with error messages
      )));
    }
    putObservable(serviceBaseURL, requestUri, body, contentType = 'application/json') {
      const authTokenName = `${this.authTokenPrefix}${serviceBaseURL}`;
      const putRequest = headers => this.apiService.putObservable(serviceBaseURL, requestUri, headers, body);
      return putRequest(this.getHeaders(contentType, authTokenName)).pipe(catchError(error => iif(() => error.status === 401, this.unauthorisedFallback(serviceBaseURL, contentType, authTokenName, putRequest),
      //Refresh token and try again
      this.getDefaultError(error) // Non-401 error, return GenericAPIResponse with error messages
      )));
    }
    deleteObservable(serviceBaseURL, requestUri, contentType = 'application/json') {
      const authTokenName = `${this.authTokenPrefix}${serviceBaseURL}`;
      const deleteRequest = headers => this.apiService.deleteObservable(serviceBaseURL, requestUri, headers);
      return deleteRequest(this.getHeaders(contentType, authTokenName)).pipe(catchError(error => iif(() => error.status === 401, this.unauthorisedFallback(serviceBaseURL, contentType, authTokenName, deleteRequest),
      //Refresh token and try again
      this.getDefaultError(error) // Non-401 error, return GenericAPIResponse with error messages
      )));
    }
    //#region iFrame functions
    getAuthTokenForIFrame(baseUrl) {
      return this.apiService.postObservable(this.serverUrl, '/token', new HttpHeaders(), {
        grant_type: 'refresh_token',
        refresh_token: localStorage.getItem(this.refreshTokenName),
        client_id: baseUrl
      }).pipe(map(response => {
        if (!response?.responseData) {
          throw new Error('Unable to refresh access token');
        }
        let grant = response.responseData;
        switch (grant.resultCode) {
          case GrantResponse.Success:
            localStorage.setItem(`${this.authTokenPrefix}${baseUrl}`, grant.access_Token);
            localStorage.setItem(this.refreshTokenName, grant.refresh_Token);
            return grant.access_Token;
          default:
            let keys = Object.keys(localStorage).filter(key => {
              return key.indexOf(this.authTokenPrefix) !== -1;
            });
            for (let key of keys) {
              localStorage.removeItem(key);
            }
            return '';
        }
      }));
    }
    //#endregion iFrame functions
    //#endregion Observable functions
    //#region Support functions
    getHeaders(contentType, authTokenName, newAuthToken = null) {
      if (newAuthToken) {
        localStorage.setItem(authTokenName, newAuthToken);
      }
      const authToken = localStorage.getItem(authTokenName);
      return new HttpHeaders({
        'Content-Type': contentType,
        'Authorization': `Bearer ${authToken}`
      });
    }
    getDefaultError(error) {
      return of({
        responseCode: error.status,
        responseMessage: error.name,
        responseError: error.message
      });
    }
    unauthorisedFallback(serviceBaseURL, contentType, authTokenName, retryRequest) {
      return this.userbaseAuthStrategy.getAuthToken(serviceBaseURL).pipe(switchMap(newAuthToken => retryRequest(this.getHeaders(contentType, authTokenName, newAuthToken))));
    }
    static {
      this.ɵfac = function AuthenticatedService_Factory(t) {
        return new (t || AuthenticatedService)(i0.ɵɵinject(i1$1.APIService), i0.ɵɵinject(i1$1.StoredSettingService), i0.ɵɵinject(CurrentUserService), i0.ɵɵinject(UserbaseAuthStrategy), i0.ɵɵinject('AuthApiBaseUrl'), i0.ɵɵinject(V1UserbaseAuthStrategy), i0.ɵɵinject(AuthTokenPrefix), i0.ɵɵinject(RefreshTokenName));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AuthenticatedService,
        factory: AuthenticatedService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AuthenticatedService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let UserService = /*#__PURE__*/(() => {
  class UserService {
    constructor(authenticatedService, http, apiBaseUrl, allowGuestUser) {
      this.authenticatedService = authenticatedService;
      this.http = http;
      this.apiBaseUrl = apiBaseUrl;
      this.allowGuestUser = allowGuestUser;
      if (this.allowGuestUser == null) {
        this.allowGuestUser = false;
      }
    }
    async getCurrentUserAsync() {
      let remoteData;
      const userResponse = await this.authenticatedService.getAsync(this.apiBaseUrl, 'api/User/CurrentUser', 'application/x-www-form-urlencoded');
      if (userResponse.responseData.id === '' || userResponse.responseData.id === undefined || this.allowGuestUser === false && userResponse.responseData.id === '00000000-0000-0000-0000-000000000000') {
        remoteData = null;
      } else {
        remoteData = userResponse.responseData;
      }
      return remoteData;
    }
    async getUserAsync(userId) {
      let remoteData;
      const userResponse = await this.authenticatedService.getAsync(this.apiBaseUrl, 'api/User/GetUser?Id=' + userId);
      if (userResponse.responseData.id === '' || userResponse.responseData.id === undefined || this.allowGuestUser === false && userResponse.responseData.id === '00000000-0000-0000-0000-000000000000') {
        remoteData = null;
      } else {
        remoteData = userResponse.responseData;
      }
      return remoteData;
    }
    async getUsers(userIds) {
      let remoteData;
      const userResponse = await this.authenticatedService.postAsync(this.apiBaseUrl, 'api/User/GetUsers', userIds);
      remoteData = userResponse.responseData;
      return remoteData;
    }
    async searchUsersByNameOrEmail(searchString) {
      const remoteData = [];
      const res = await this.authenticatedService.getAsync(this.apiBaseUrl, 'api/User/SearchUserSummaries?searchString=' + searchString);
      if (res.responseCode === 200) {
        return res.responseData;
      }
      return remoteData;
    }
    async getUsersFromRole(roleName) {
      const remoteData = [];
      const res = await this.authenticatedService.getAsync(this.apiBaseUrl, "api/User/UsersFromRole/" + roleName);
      if (res.responseCode === 200) {
        return res.responseData;
      }
      return remoteData;
    }
    async getUserDetailsAsync(userId) {
      let remoteData;
      const res = await this.authenticatedService.getAsync(this.apiBaseUrl, 'api/User/GetUser?Id=' + userId);
      if (res.responseData.id === '' || res.responseData.id === undefined || this.allowGuestUser === false && res.responseData.id === '00000000-0000-0000-0000-000000000000') {
        remoteData = null;
      } else {
        remoteData = res.responseData;
      }
      return remoteData;
    }
    getCurrentUserObservable() {
      return this.authenticatedService.getObservable(this.apiBaseUrl, 'api/user/currentuser', 'application/x-www-form-urlencoded').pipe(map(res => {
        if (res && res.responseData.id === '' || res?.responseData.id === undefined || !this.allowGuestUser && res.responseData.id === '00000000-0000-0000-0000-000000000000') {
          return null;
        } else {
          return res.responseData;
        }
      }));
    }
    getUserObservable(userId) {
      return this.authenticatedService.getObservable(this.apiBaseUrl, `api/user/getuser?Id=${userId}`).pipe(map(res => {
        if (res && res.responseData.id === '' || res?.responseData.id === undefined || !this.allowGuestUser && res.responseData.id === '00000000-0000-0000-0000-000000000000') {
          return null;
        } else {
          return res.responseData;
        }
      }));
    }
    async registerAsync(user) {
      try {
        let remoteData;
        const userResponse = await this.authenticatedService.postAsync(this.apiBaseUrl, 'api/Account/Register', user, 'application/json');
        if (userResponse.responseData.id === '' || userResponse.responseData.id === undefined || this.allowGuestUser === false && userResponse.responseData.id === '00000000-0000-0000-0000-000000000000') {
          remoteData = null;
        } else {
          remoteData = userResponse.responseData;
        }
        return remoteData;
      } catch (error) {
        return null;
      }
    }
    async updateUserAsync(user) {
      let remoteData;
      const userResponse = await this.authenticatedService.postAsync(this.apiBaseUrl, 'api/User/Update', user, 'application/json');
      if (userResponse.responseData.id === '' || userResponse.responseData.id === undefined || this.allowGuestUser === false && userResponse.responseData.id === '00000000-0000-0000-0000-000000000000') {
        remoteData = null;
      } else {
        remoteData = userResponse.responseData;
      }
      return remoteData;
    }
    async updateUserDetailsAsync(user) {
      let remoteData;
      const userResponse = await this.authenticatedService.postAsync(this.apiBaseUrl, 'api/User/UpdateUserDetails', user, 'application/json');
      if (userResponse.responseData.id === '' || userResponse.responseData.id === undefined || this.allowGuestUser === false && userResponse.responseData.id === '00000000-0000-0000-0000-000000000000') {
        remoteData = null;
      } else {
        remoteData = userResponse.responseData;
      }
      return remoteData;
    }
    async addRoleToUser(userId, roleId) {
      let _userRole = new UserRoleDto();
      _userRole.userId = userId;
      _userRole.roleId = roleId;
      let valid = await this.authenticatedService.postAsync(this.apiBaseUrl, 'api/User/AddRoleToUser', _userRole);
      return valid.responseCode == 200;
    }
    async removeRoleFromUser(userId, roleId) {
      let _userRole = new UserRoleDto();
      _userRole.userId = userId;
      _userRole.roleId = roleId;
      let valid = await this.authenticatedService.postAsync(this.apiBaseUrl, 'api/User/RemoveRoleFromUser', _userRole);
      return valid.responseCode == 200;
    }
    async isEmailValid(emailAddress, userId) {
      let _email = new ValidEmailCheck();
      _email.emailAddress = emailAddress;
      _email.userId = userId;
      let valid = await this.authenticatedService.postTypedAsync(this.apiBaseUrl, 'api/User/IsEmailValid', _email);
      return valid;
    }
    async uploadSignatureImage(userId, signatureImage) {
      let _path = "";
      let formData = new FormData();
      formData.append('Image', signatureImage, signatureImage.name);
      formData.append('UserId', userId);
      let headers = new HttpHeaders();
      /** In Angular 5, including the header Content-Type can invalidate your request */
      headers.append('Content-Type', 'multipart/form-data');
      headers.append('Accept', 'application/json');
      let options = {
        headers: headers
      };
      lastValueFrom(this.http.post(this.apiBaseUrl + 'api/User/UploadSignatureImage', formData, options)).then(res => {
        let responseObj = res;
        console.log(responseObj);
        console.log(responseObj.body ? responseObj.body : res);
        _path = (responseObj.body ? responseObj.body : responseObj).toString();
      }, err => {
        _path = "";
      });
      return _path;
    }
    static {
      this.ɵfac = function UserService_Factory(t) {
        return new (t || UserService)(i0.ɵɵinject(AuthenticatedService), i0.ɵɵinject(i2$1.HttpClient), i0.ɵɵinject('UserApiBaseUrl'), i0.ɵɵinject('AllowGuestUser', 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UserService,
        factory: UserService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UserService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AuthenticationService = /*#__PURE__*/(() => {
  class AuthenticationService {
    constructor(easyXDMService, serverURL, loginURL, loginGuestURL, registerUrl, storedSettingService, authenticatedService, currentUserService, authRedirectUrl, router, apiService, serverUrl, userBaseAuthStrategy, authTokenPrefix, refreshTokenName) {
      this.easyXDMService = easyXDMService;
      this.serverURL = serverURL;
      this.loginURL = loginURL;
      this.loginGuestURL = loginGuestURL;
      this.registerUrl = registerUrl;
      this.storedSettingService = storedSettingService;
      this.authenticatedService = authenticatedService;
      this.currentUserService = currentUserService;
      this.authRedirectUrl = authRedirectUrl;
      this.router = router;
      this.apiService = apiService;
      this.serverUrl = serverUrl;
      this.userBaseAuthStrategy = userBaseAuthStrategy;
      this.authTokenPrefix = authTokenPrefix;
      this.refreshTokenName = refreshTokenName;
    }
    // Only for Backwards Compatibility V1
    async getAuthTokenAsync(baseUrl) {
      const returnString = await this.easyXDMService.MsgPromise(this.serverUrl, baseUrl, 'DistLogin');
      return returnString;
    }
    login(loginFormContainer) {
      let remoteData;
      remoteData = new Observable(observer => {
        this.easyXDMService.IFrameObservable(this.loginURL, loginFormContainer, '400px', '450px', 'CheckoutLogin').subscribe(value => {
          observer.next(value);
          observer.complete();
        });
      });
      return remoteData;
    }
    logOut() {
      let remoteData;
      remoteData = new Observable(observer => {
        this.easyXDMService.MsgObservable(this.serverURL, 'Logout', 'CheckoutLogin').subscribe(value => {
          this.clearAuthTokens();
          observer.next(true);
          observer.complete();
        });
      });
      return remoteData;
    }
    setRoleLinkId(roleLinkId) {
      let remoteData;
      remoteData = new Observable(observer => {
        this.easyXDMService.MsgObservable(this.serverURL, 'SetRoleLinkId:' + roleLinkId, 'CheckoutLogin').subscribe(value => {
          this.clearAuthTokens();
          observer.next(true);
          observer.complete();
        });
      });
      return remoteData;
    }
    getRoleLinkId() {
      let remoteData;
      remoteData = new Observable(observer => {
        this.easyXDMService.MsgObservable(this.serverURL, 'GetRoleLinkId', 'CheckoutLogin').subscribe(value => {
          this.clearAuthTokens();
          observer.next(value);
          observer.complete();
        });
      });
      return remoteData;
    }
    async loginAsync(loginFormContainer) {
      const returnString = await this.easyXDMService.IFramePromise(this.loginURL, loginFormContainer, '100%', '460px', 'CheckoutLogin');
      return returnString;
    }
    async registerAsync(registerFormContainer) {
      const returnString = await this.easyXDMService.IFramePromise(this.registerUrl, registerFormContainer, '355px', '465px', 'CheckoutLogin');
      return returnString;
    }
    async loginGuestAsync(guestLoginFormContainer) {
      const returnString = await this.easyXDMService.IFramePromise(this.loginGuestURL, guestLoginFormContainer, '240px', '45px', 'CheckoutLogin');
      return returnString;
    }
    async logOutAsync() {
      this.clearAuthTokens();
      await this.easyXDMService.MsgPromise(this.serverURL, 'Logout', 'CheckoutLogin');
      return true;
    }
    async loginGuestBackgroundAsync() {
      this.clearAuthTokens();
      await this.easyXDMService.MsgPromise(this.serverURL, 'GuestLogin', 'DistLogin');
      return true;
    }
    async setRoleLinkIdAsync(roleLinkId) {
      this.clearAuthTokens();
      await this.easyXDMService.MsgPromise(this.serverURL, 'SetRoleLinkId:' + roleLinkId, 'CheckoutLogin');
      return true;
    }
    async getRoleLinkIdAsync() {
      this.clearAuthTokens();
      let _roleLinkId = await this.easyXDMService.MsgPromise(this.serverURL, 'GetRoleLinkId', 'CheckoutLogin');
      return _roleLinkId;
    }
    clearAuthTokens() {
      let keys = Object.keys(localStorage).filter(key => {
        return key.indexOf(this.authTokenPrefix) !== -1;
      });
      for (let key of keys) {
        localStorage.removeItem(key);
      }
    }
    clearRefreshToken() {
      let keys = Object.keys(localStorage).filter(key => {
        return key.indexOf(this.refreshTokenName) !== -1;
      });
      for (let key of keys) {
        localStorage.removeItem(key);
      }
    }
    //#region V2 
    logout() {
      return this.authenticatedService.postObservable(this.serverURL, '/api/user/logout', null).pipe(map(response => {
        return response?.responseCode;
      }));
    }
    async init(activatedRouteSnapshot, routerSnapshot) {
      const urlTree = this.parseIncomingAuthTokens(activatedRouteSnapshot, routerSnapshot);
      if (urlTree) {
        return urlTree;
      }
      await this.currentUserService.loadCurrentUser();
      const currentUser = this.currentUserService.getCurrentUser();
      if (!currentUser.user) {
        window.location.href = `${this.authRedirectUrl}?returnUrl=${encodeURI(location.origin)}${encodeURIComponent(routerSnapshot.url)}`;
        // Maybe make a page that says "redirecting" or something
        return this.router.createUrlTree(['']);
      }
      return null;
    }
    parseIncomingAuthTokens(activatedRouteSnapshot, routerSnapshot) {
      let authToken = routerSnapshot.root.queryParams['auth'];
      let refreshToken = routerSnapshot.root.queryParams['refresh'];
      if (authToken) {
        localStorage.setItem(`${this.authTokenPrefix}${location.origin}`, authToken);
      }
      if (refreshToken) {
        localStorage.setItem(this.refreshTokenName, refreshToken);
      }
      const filterKeys = ['auth', 'refresh'];
      const keysToRemove = [];
      filterKeys.forEach(key => {
        activatedRouteSnapshot.root.queryParams[key] && keysToRemove.push(key);
      });
      if (keysToRemove.length) {
        return this.removeParamsFromUrl(activatedRouteSnapshot, routerSnapshot, keysToRemove);
      } else {
        return null;
      }
    }
    removeParamsFromUrl(activatedRouteSnapshot, routerSnapshot, keysToRemove) {
      const queryParams = {};
      const keysToKeep = [];
      Object.entries(activatedRouteSnapshot.root.queryParams).forEach(([key, value]) => {
        if (!keysToRemove.includes(key)) {
          keysToKeep.push(key);
        }
      });
      keysToKeep.forEach(key => {
        queryParams[key] = activatedRouteSnapshot.root.queryParams[key];
      });
      let url = routerSnapshot.url;
      if (url.includes('?')) {
        url = url.substring(0, url.indexOf('?'));
      }
      return this.router.createUrlTree([url], {
        queryParams
      });
    }
    //#endregion
    //#region V2 Backwards Compatibility
    loginAsGuest() {
      const headers = new HttpHeaders().append('Content-Type', 'application/json');
      return this.apiService.postObservable(this.serverURL, '/token', headers, {
        grant_type: 'password',
        username: 'Guestuser'
      }).pipe(map(response => {
        if (!response) {
          throw 'Unable to login as guest';
        }
        const grant = response.responseData;
        localStorage.setItem(this.authTokenPrefix, grant.access_Token);
        localStorage.setItem(this.refreshTokenName, grant.refresh_Token);
        localStorage.setItem('authLoginId', grant.remoteLoginId);
      }));
    }
    remoteLogin(remoteLoginId) {
      const headers = new HttpHeaders().append('Content-Type', 'application/json');
      return this.apiService.postObservable(this.serverURL, '/token', headers, {
        grant_type: 'password',
        remoteLogin_id: remoteLoginId
      }).pipe(map(response => {
        if (!response) {
          throw 'Unable to login as guest';
        }
        const grant = response.responseData;
        localStorage.setItem(this.authTokenPrefix, grant.access_Token);
        localStorage.setItem(this.refreshTokenName, grant.refresh_Token);
        localStorage.setItem('authLoginId', grant.remoteLoginId);
        return true;
      })).pipe(catchError(_ => {
        localStorage.removeItem(this.authTokenPrefix);
        localStorage.removeItem(this.refreshTokenName);
        return of(false);
      }));
    }
    static {
      this.ɵfac = function AuthenticationService_Factory(t) {
        return new (t || AuthenticationService)(i0.ɵɵinject(i1$1.EasyXDMService), i0.ɵɵinject('AuthApiBaseUrl'), i0.ɵɵinject('LoginPageUrl'), i0.ɵɵinject('LoginGuestPageUrl'), i0.ɵɵinject('RegisterPageUrl'), i0.ɵɵinject(i1$1.StoredSettingService), i0.ɵɵinject(AuthenticatedService), i0.ɵɵinject(CurrentUserService), i0.ɵɵinject("AuthRedirectUrl"), i0.ɵɵinject(i2.Router), i0.ɵɵinject(i1$1.APIService), i0.ɵɵinject('AuthApiBaseUrl'), i0.ɵɵinject(UserbaseAuthStrategy), i0.ɵɵinject(AuthTokenPrefix), i0.ɵɵinject(RefreshTokenName));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AuthenticationService,
        factory: AuthenticationService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AuthenticationService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let LoginComponent = /*#__PURE__*/(() => {
  class LoginComponent {
    constructor(currentUserService, userService, authService, platformId) {
      this.currentUserService = currentUserService;
      this.userService = userService;
      this.authService = authService;
      this.platformId = platformId;
    }
    ngOnInit() {
      this.getCurrentUser();
    }
    ngAfterViewInit() {
      this.showLogin();
    }
    async getCurrentUser() {
      try {
        this.currentUserService.loadCurrentUser();
      } catch (error) {
        await this.authService.logOutAsync();
        this.currentUserService.loadCurrentUser();
      }
    }
    async showLogin() {
      try {
        if (this.platformId.isBrowser) {
          const res = await this.authService.loginAsync('highfieldauth-authenticationLogin');
        }
        this.getCurrentUser();
      } catch (err) {
        console.log(err);
      }
    }
    static {
      this.ɵfac = function LoginComponent_Factory(t) {
        return new (t || LoginComponent)(i0.ɵɵdirectiveInject(CurrentUserService), i0.ɵɵdirectiveInject(UserService), i0.ɵɵdirectiveInject(AuthenticationService), i0.ɵɵdirectiveInject(i1.Platform));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: LoginComponent,
        selectors: [["highfieldauth-login-page"]],
        decls: 1,
        vars: 0,
        consts: [["id", "highfieldauth-authenticationLogin"]],
        template: function LoginComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelement(0, "div", 0);
          }
        },
        encapsulation: 2
      });
    }
  }
  return LoginComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GuestLoginComponent = /*#__PURE__*/(() => {
  class GuestLoginComponent {
    constructor(currentUserService, userService, authService) {
      this.currentUserService = currentUserService;
      this.userService = userService;
      this.authService = authService;
    }
    ngOnInit() {
      this.getCurrentUser();
    }
    ngAfterViewInit() {
      this.showLogin();
    }
    async getCurrentUser() {
      try {
        const user = await this.userService.getCurrentUserAsync();
        this.currentUserService.setCurrentUser(user);
      } catch (error) {
        this.currentUserService.setCurrentUser(null);
      }
    }
    async showLogin() {
      try {
        const res = await this.authService.loginGuestAsync('highfieldauth-authenticationGuestLogin');
        this.getCurrentUser();
      } catch (err) {
        console.log(err);
      }
    }
    static {
      this.ɵfac = function GuestLoginComponent_Factory(t) {
        return new (t || GuestLoginComponent)(i0.ɵɵdirectiveInject(CurrentUserService), i0.ɵɵdirectiveInject(UserService), i0.ɵɵdirectiveInject(AuthenticationService));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: GuestLoginComponent,
        selectors: [["highfieldauth-guest-login-page"]],
        decls: 1,
        vars: 0,
        consts: [["id", "highfieldauth-authenticationGuestLogin"]],
        template: function GuestLoginComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelement(0, "div", 0);
          }
        },
        encapsulation: 2
      });
    }
  }
  return GuestLoginComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let RegisterComponent = /*#__PURE__*/(() => {
  class RegisterComponent {
    constructor(currentUserService, userService, authService) {
      this.currentUserService = currentUserService;
      this.userService = userService;
      this.authService = authService;
    }
    ngOnInit() {
      this.getCurrentUser();
    }
    ngAfterViewInit() {
      this.showRegistration();
    }
    async getCurrentUser() {
      try {
        const user = await this.userService.getCurrentUserAsync();
        this.currentUserService.setCurrentUser(user);
      } catch (error) {
        this.currentUserService.setCurrentUser(null);
      }
    }
    async showRegistration() {
      try {
        const res = await this.authService.registerAsync('highfieldauth-authenticationRegister');
        this.getCurrentUser();
      } catch (err) {
        console.log(err);
      }
    }
    static {
      this.ɵfac = function RegisterComponent_Factory(t) {
        return new (t || RegisterComponent)(i0.ɵɵdirectiveInject(CurrentUserService), i0.ɵɵdirectiveInject(UserService), i0.ɵɵdirectiveInject(AuthenticationService));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: RegisterComponent,
        selectors: [["highfieldauth-register-page"]],
        decls: 1,
        vars: 0,
        consts: [["id", "highfieldauth-authenticationRegister"]],
        template: function RegisterComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelement(0, "div", 0);
          }
        },
        encapsulation: 2
      });
    }
  }
  return RegisterComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AngularHighfieldauthModule = /*#__PURE__*/(() => {
  class AngularHighfieldauthModule {
    static forRoot() {
      return {
        ngModule: AngularHighfieldauthModule
      };
    }
    static {
      this.ɵfac = function AngularHighfieldauthModule_Factory(t) {
        return new (t || AngularHighfieldauthModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: AngularHighfieldauthModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CommonModule]
      });
    }
  }
  return AngularHighfieldauthModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class User {
  constructor(id, userName, hasPassword, securityStamp, email, emailConfirmed, firstName, lastName, roles) {
    this.id = id;
    this.userName = userName;
    this.hasPassword = hasPassword;
    this.securityStamp = securityStamp;
    this.email = email;
    this.emailConfirmed = emailConfirmed;
    this.firstName = firstName;
    this.lastName = lastName;
    this.roles = roles;
  }
  hasRole(role) {
    if (Array.isArray(this.roles)) {
      return this.roles.indexOf(role) > -1;
    } else {
      return this.roles == role;
    }
  }
}
class Role {
  constructor(id, name, description) {
    this.emailOnAssignment = false;
    this.userRole = false;
    this.centreRole = false;
    this.systemRole = false;
    this.tasklistRole = false;
    this.id = id;
    this.name = name;
    this.description = description;
  }
}
class NewUser {}
class UserDetails {
  constructor(id, userName, email, firstName, lastName, dateOfBirth, address1, address2, town, countyID, postCode, telephone, mobile, vehicleRegistration) {
    this.id = id;
    this.userName = userName;
    this.email = email;
    this.firstName = firstName;
    this.lastName = lastName;
    this.dateOfBirth = dateOfBirth;
    this.address1 = address1;
    this.address2 = address2;
    this.town = town;
    this.countyID = countyID;
    this.postCode = postCode;
    this.telephone = telephone;
    this.mobile = mobile;
    this.vehicleRegistration = vehicleRegistration;
  }
}
let AuthGuardService = /*#__PURE__*/(() => {
  class AuthGuardService {
    constructor(router, currentUserService, authenticatedFailedRedirect, authorisedFailedRedirect) {
      this.router = router;
      this.currentUserService = currentUserService;
      this.authenticatedFailedRedirect = authenticatedFailedRedirect;
      this.authorisedFailedRedirect = authorisedFailedRedirect;
    }
    canActivate(route, state) {
      let currentUser = this.currentUserService.getCurrentUser();
      if (currentUser.user) {
        var hasPermission = this.hasRequiredPermission(route, currentUser);
        if (hasPermission) {
          return true;
        } else {
          this.router.navigate([this.authorisedFailedRedirect], {
            queryParams: {
              returnUrl: state.url
            }
          });
          return false;
        }
      }
      this.router.navigate([this.authenticatedFailedRedirect], {
        queryParams: {
          returnUrl: state.url
        }
      });
      return false;
    }
    hasRequiredPermission(routeData, currentUser) {
      let authRoles = routeData.data['auth'];
      let hasPermissions = false;
      // If no specified roles, then it's an open page, let anyone through. 
      if (!authRoles) {
        return true;
      }
      if (Array.isArray(authRoles)) {
        for (let role of authRoles) {
          if (currentUser.user?.hasRole(role)) {
            hasPermissions = true;
            break;
          }
        }
      } else {
        hasPermissions = currentUser.user?.hasRole(authRoles);
      }
      return hasPermissions;
    }
    static {
      this.ɵfac = function AuthGuardService_Factory(t) {
        return new (t || AuthGuardService)(i0.ɵɵinject(i2.Router), i0.ɵɵinject(CurrentUserService), i0.ɵɵinject('AuthenticatedCheckFailURL'), i0.ɵɵinject('AuthorisedCheckFailURL'));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AuthGuardService,
        factory: AuthGuardService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AuthGuardService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let RoleService = /*#__PURE__*/(() => {
  class RoleService {
    constructor(authenticatedService, apiBaseUrl, allowGuestUser) {
      this.authenticatedService = authenticatedService;
      this.apiBaseUrl = apiBaseUrl;
      this.allowGuestUser = allowGuestUser;
      if (this.allowGuestUser == null) {
        this.allowGuestUser = false;
      }
    }
    async getRolesAsync() {
      let remoteData = [];
      const res = await this.authenticatedService.getAsync(this.apiBaseUrl, 'api/Roles/GetRoles');
      if (res.responseCode === 200) {
        remoteData = res.responseData;
      }
      return remoteData;
    }
    async getRoleAsync(roleId) {
      let remoteData = null;
      const res = await this.authenticatedService.getAsync(this.apiBaseUrl, 'api/Roles/GetRole?Id=' + roleId);
      if (res.responseData.id === '' || res.responseData.id === undefined || this.allowGuestUser === false && res.responseData.id === '00000000-0000-0000-0000-000000000000') {
        remoteData = null;
      } else {
        remoteData = res.responseData;
      }
      return remoteData;
    }
    async getRolesForUserAsync(userId) {
      let remoteData = [];
      const res = await this.authenticatedService.getAsync(this.apiBaseUrl, 'api/Roles/GetRolesForUser?userId=' + userId);
      if (res.responseCode === 200) {
        remoteData = res.responseData;
      }
      return remoteData;
    }
    async getRolesBasedOnCondition(conditions) {
      let roles = await this.authenticatedService.postTypedAsync(this.apiBaseUrl, '/api/Roles/GetRolesBasedOnCondition', conditions);
      return roles ?? [];
    }
    static {
      this.ɵfac = function RoleService_Factory(t) {
        return new (t || RoleService)(i0.ɵɵinject(AuthenticatedService), i0.ɵɵinject('UserApiBaseUrl'), i0.ɵɵinject('AllowGuestUser', 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: RoleService,
        factory: RoleService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return RoleService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class GetRolesByConditionRequestModel {
  constructor(markingRole, centreRole, systemRole, taskListRole, userRole) {
    this.markingRole = markingRole;
    this.centreRole = centreRole;
    this.systemRole = systemRole;
    this.taskListRole = taskListRole;
    this.userRole = userRole;
  }
}
let V2UserbaseAuthStrategy = /*#__PURE__*/(() => {
  class V2UserbaseAuthStrategy extends BaseUserbaseAuthStrategy {
    constructor(easyXDMService, apiService, serverUrl, authTokenPrefix, refreshTokenName) {
      super(easyXDMService, serverUrl);
      this.easyXDMService = easyXDMService;
      this.apiService = apiService;
      this.serverUrl = serverUrl;
      this.authTokenPrefix = authTokenPrefix;
      this.refreshTokenName = refreshTokenName;
    }
    async getTokenForCurrentUser(baseUrl) {
      return new Promise(resolve => {
        resolve(localStorage.getItem(`${this.authTokenPrefix}${location.origin}`));
      });
    }
    getAuthToken(baseUrl) {
      return this.apiService.postObservable(this.serverUrl, '/token', new HttpHeaders(), {
        grant_type: 'refresh_token',
        refresh_token: localStorage.getItem(this.refreshTokenName),
        client_id: baseUrl
      }).pipe(map$1(response => {
        if (!response?.responseData) {
          throw new Error('Unable to refresh access token');
        }
        let grant = response.responseData;
        switch (grant.resultCode) {
          case GrantResponse.Success:
            localStorage.setItem(`${this.authTokenPrefix}${baseUrl}`, grant.access_Token);
            localStorage.setItem(this.refreshTokenName, grant.refresh_Token);
            return grant.access_Token;
          default:
            localStorage.clear();
            throw new Error(`Unable to refresh access token - ${grant.resultMessage}`);
        }
      }));
    }
    static {
      this.ɵfac = function V2UserbaseAuthStrategy_Factory(t) {
        return new (t || V2UserbaseAuthStrategy)(i0.ɵɵinject(i1$1.EasyXDMService), i0.ɵɵinject(i1$1.APIService), i0.ɵɵinject('AuthApiBaseUrl'), i0.ɵɵinject(AuthTokenPrefix), i0.ɵɵinject(RefreshTokenName));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: V2UserbaseAuthStrategy,
        factory: V2UserbaseAuthStrategy.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return V2UserbaseAuthStrategy;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of angular-highfieldauth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AngularHighfieldauthModule, AuthCheckComponent, AuthGuardService, AuthTokenPrefix, AuthenticatedService, AuthenticationService, CurrentUser, CurrentUserService, GetRolesByConditionRequestModel, GuestLoginComponent, LoginComponent, LoginUser, NewUser, RefreshTokenName, RegisterComponent, Role, RoleService, User, UserDetails, UserRoleDto, UserService, UserbaseAuthStrategy, V1AuthTokenPrefix, V1RefreshTokenName, V1UserbaseAuthStrategy, V2AuthTokenPrefix, V2RefreshTokenName, V2UserbaseAuthStrategy, ValidEmailCheck };
